import { Link, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { LogOut, X, ChevronDown, Check, Moon, Sun } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent } from './ui/sheet';
import { useLanguage } from '../context/LanguageContext';
import { useRegion } from '../context/RegionContext';
import { useTheme } from '../context/ThemeContext';

// Logo URLs - CloudGM Business (Light/Dark variants)
const LOGO_LIGHT = "/logo-light.png"; // Black text for light theme
const LOGO_DARK = "/logo-dark.png";   // White text for dark theme

export const Navbar = ({ user, onLogout, showSidebarToggle, sidebarOpen, setSidebarOpen }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showMobileSettings, setShowMobileSettings] = useState(false);
  const settingsMenuRef = useRef(null);
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();
  const { region, getAllRegions, changeRegion, currency, getAllCurrencies, changeCurrency } = useRegion();
  const { isDarkMode, toggleDarkMode } = useTheme();

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  const allRegions = getAllRegions();
  const allCurrencies = getAllCurrencies();

  // Desktop-only click-outside: closes the DESKTOP dropdown when clicking
  // elsewhere. The MOBILE sheet is now handled by Radix Sheet (shadcn/ui),
  // which correctly manages focus, outside-clicks and touch events on real
  // devices — no manual mousedown listener needed.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target)) {
        setShowSettingsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // iter-236: On mobile, the settings sheet is a Radix Dialog under the hood.
  // Radix already applies overflow:hidden to <body> while open, so no
  // manual scroll-lock is needed here.

  // Simple onClick tap handler. No preventDefault, no stopPropagation.
  // Radix Sheet handles the outside-click / escape / focus logic for us.
  const tapProps = (fn) => ({
    type: 'button',
    onClick: () => fn(),
  });

  // Settings dropdown content component
  const SettingsDropdownContent = ({ onClose }) => (
    <div className={`rounded-2xl shadow-2xl border overflow-hidden w-full min-w-0 lg:min-w-[280px] lg:w-max ${
      isDarkMode 
        ? 'bg-[#131B2C] border-white/10' 
        : 'bg-white border-black/5'
    }`}>
      {/* Dark Mode Section */}
      <div className={`p-4 border-b ${isDarkMode ? 'border-white/10' : 'border-black/5'}`}>
        <p className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {language === 'ar' ? 'الوضع الليلي' : 'Dark Mode'}
        </p>
        <button
          {...tapProps(() => toggleDarkMode())}
          className={`w-full px-3 py-2.5 flex items-center gap-3 rounded-xl transition-all duration-300 ${
            isDarkMode 
              ? 'hover:bg-white/10' 
              : 'hover:bg-gray-50'
          }`}
          data-testid="dark-mode-toggle-row"
        >
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-amber-400 to-orange-500' : 'bg-gradient-to-br from-[#38BDF8] to-[#F97316]'}`}>
            {isDarkMode ? (
              <Sun className="h-4 w-4 text-white" />
            ) : (
              <Moon className="h-4 w-4 text-white" />
            )}
          </div>
          <span className={`text-sm flex-1 text-start ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {isDarkMode 
              ? (language === 'ar' ? 'تفعيل الوضع النهاري' : 'Switch to Light Mode')
              : (language === 'ar' ? 'تفعيل الوضع الليلي' : 'Switch to Dark Mode')
            }
          </span>
          <div className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${isDarkMode ? 'bg-gradient-to-r from-[#38BDF8] to-[#F97316]' : 'bg-gray-300'}`}>
            <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-lg transition-transform duration-300 ${isDarkMode ? 'translate-x-6' : 'translate-x-0'}`}></div>
          </div>
        </button>
      </div>

      {/* Language Section */}
      <div className={`p-4 border-b ${isDarkMode ? 'border-white/10' : 'border-black/5'}`}>
        <p className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {language === 'ar' ? 'تغيير اللغة' : 'Change Language'}
        </p>
        <div className="space-y-1">
          <button
            {...tapProps(() => {
              if (language !== 'en') toggleLanguage();
              onClose?.();
            })}
            className={`w-full px-3 py-2.5 flex items-center gap-3 rounded-xl transition-all duration-300 cursor-pointer ${
              isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-50'
            } ${language === 'en' ? (isDarkMode ? 'bg-gradient-to-r from-[#38BDF8]/20 to-[#F97316]/20' : 'bg-gradient-to-r from-[#38BDF8]/10 to-[#F97316]/10') : ''}`}
            data-testid="lang-english-row"
          >
            <span className={`text-lg font-bold w-6 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>EN</span>
            <span className={`text-sm flex-1 text-start ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>English</span>
            {language === 'en' && <Check className="h-4 w-4 text-[#38BDF8] flex-shrink-0" />}
          </button>
          <button
            {...tapProps(() => {
              if (language !== 'ar') toggleLanguage();
              onClose?.();
            })}
            className={`w-full px-3 py-2.5 flex items-center gap-3 rounded-xl transition-all duration-300 cursor-pointer ${
              isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-50'
            } ${language === 'ar' ? (isDarkMode ? 'bg-gradient-to-r from-[#38BDF8]/20 to-[#F97316]/20' : 'bg-gradient-to-r from-[#38BDF8]/10 to-[#F97316]/10') : ''}`}
            data-testid="lang-arabic-row"
          >
            <span className={`text-lg font-bold w-6 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>ع</span>
            <span className={`text-sm flex-1 text-start ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>العربية</span>
            {language === 'ar' && <Check className="h-4 w-4 text-[#38BDF8] flex-shrink-0" />}
          </button>
        </div>
      </div>

      {/* Currency Section */}
      <div className={`p-4 border-b ${isDarkMode ? 'border-white/10' : 'border-black/5'}`}>
        <p className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {language === 'ar' ? 'تغيير العملة' : 'Change Currency'}
        </p>
        <div className="space-y-1">
          {allCurrencies.map((c) => (
            <button
              key={c.id}
              {...tapProps(() => {
                changeCurrency(c.id);
                onClose?.();
              })}
              className={`w-full px-3 py-2.5 flex items-center gap-3 rounded-xl transition-all duration-300 cursor-pointer ${
                isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-50'
              } ${currency.id === c.id ? (isDarkMode ? 'bg-gradient-to-r from-[#38BDF8]/20 to-[#F97316]/20' : 'bg-gradient-to-r from-[#38BDF8]/10 to-[#F97316]/10') : ''}`}
              data-testid={`currency-${c.id}-row`}
            >
              {c.symbolImage ? (
                <img src={c.symbolImage} alt={c.code} className="h-4 w-4 object-contain flex-shrink-0" />
              ) : (
                <span className={`text-lg font-bold w-4 text-center flex-shrink-0 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{c.symbol}</span>
              )}
              <span className={`text-sm flex-1 text-start ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {c.code} - {language === 'ar' ? c.name : c.nameEn}
              </span>
              {currency.id === c.id && <Check className="h-4 w-4 text-[#38BDF8] flex-shrink-0" />}
            </button>
          ))}
        </div>
      </div>

      {/* Region/Country Section */}
      <div className="p-4">
        <p className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {language === 'ar' ? 'الدولة/المنطقة' : 'Country/Region'}
        </p>
        <div className="space-y-1">
          {allRegions.map((r) => (
            <button
              key={r.id}
              {...tapProps(() => {
                changeRegion(r.id);
                onClose?.();
              })}
              className={`w-full px-3 py-2.5 flex items-center gap-3 rounded-xl transition-all duration-300 cursor-pointer ${
                isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-50'
              } ${region.id === r.id ? (isDarkMode ? 'bg-gradient-to-r from-[#38BDF8]/20 to-[#F97316]/20' : 'bg-gradient-to-r from-[#38BDF8]/10 to-[#F97316]/10') : ''}`}
              data-testid={`region-${r.id}-row`}
            >
              <img src={r.flagImage} alt={r.nameEn} className="h-5 w-7 object-cover rounded-sm flex-shrink-0" />
              <span className={`text-sm font-medium flex-1 text-start ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {language === 'ar' ? r.name : r.nameEn}
              </span>
              {region.id === r.id && <Check className="h-4 w-4 text-[#38BDF8] flex-shrink-0" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${
      isDarkMode 
        ? 'glass-nav' 
        : 'glass-nav'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <img 
              src={isDarkMode ? LOGO_DARK : LOGO_LIGHT} 
              alt="CloudGM Business" 
              className="h-12 sm:h-14 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Settings & Auth */}
          <div className="hidden lg:flex items-center gap-3">
            <div className="relative" ref={settingsMenuRef}>
              <button
                onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                className={`flex items-center gap-2.5 h-11 px-4 rounded-xl transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-white/5 hover:bg-white/10 border border-white/10' 
                    : 'bg-white/80 hover:bg-white border border-black/5 shadow-sm'
                }`}
                data-testid="settings-selector"
              >
                {/* Dark Mode Icon */}
                <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-gradient-to-br from-amber-400/20 to-orange-500/20' : 'bg-gradient-to-br from-[#38BDF8]/10 to-[#F97316]/10'}`} data-testid="dark-mode-icon">
                  {isDarkMode ? <Sun className="h-3.5 w-3.5 text-amber-400" /> : <Moon className="h-3.5 w-3.5 text-[#38BDF8]" />}
                </div>
                <div className={`w-px h-5 ${isDarkMode ? 'bg-white/10' : 'bg-black/10'}`}></div>
                <img src={region.flagImage} alt={region.nameEn} className="h-4 w-6 object-cover rounded" />
                {(isDarkMode ? currency.symbolImageDark : currency.symbolImageLight) ? (
                  <img 
                    src={isDarkMode ? currency.symbolImageDark : currency.symbolImageLight} 
                    alt={currency.code} 
                    className="h-5 w-5 object-contain" 
                  />
                ) : (
                  <span className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{currency.symbol}</span>
                )}
                <span className={`font-semibold text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{language.toUpperCase()}</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} ${showSettingsMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showSettingsMenu && (
                <div className="absolute top-full mt-2 end-0 z-50 animate-fade-in-scale">
                  {SettingsDropdownContent({ onClose: () => setShowSettingsMenu(false) })}
                </div>
              )}
            </div>

            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/dashboard">
                  <button className="btn-gradient text-sm h-11 px-5 font-medium" data-testid="nav-dashboard">
                    {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
                  </button>
                </Link>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <button 
                    className={`h-11 px-5 text-sm font-medium rounded-xl transition-all duration-300 ${
                      isDarkMode 
                        ? 'text-gray-300 hover:text-white hover:bg-white/10 border border-white/20' 
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 border border-gray-200'
                    }`} 
                    data-testid="nav-login"
                  >
                    {t('login')}
                  </button>
                </Link>
                <Link to="/register">
                  <button className="btn-gradient text-sm h-11 px-5 font-medium" data-testid="nav-register">
                    {t('register')}
                  </button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Header Buttons */}
          <div className="lg:hidden flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowMobileSettings(!showMobileSettings)}
                className={`flex items-center gap-1.5 px-2.5 py-2 rounded-xl min-h-[40px] transition-colors ${
                  isDarkMode 
                    ? 'bg-white/5 border border-white/10' 
                    : 'bg-white/80 border border-black/5'
                }`}
                data-testid="settings-toggle-mobile"
              >
                {/* Dark Mode Icon */}
                <div className={`p-1 rounded-lg ${isDarkMode ? 'bg-amber-500/20' : 'bg-[#38BDF8]/10'}`}>
                  {isDarkMode ? <Sun className="h-3 w-3 text-amber-400" /> : <Moon className="h-3 w-3 text-[#38BDF8]" />}
                </div>
                <div className={`w-px h-3.5 ${isDarkMode ? 'bg-white/10' : 'bg-black/10'}`}></div>
                <img src={region.flagImage} alt={region.nameEn} className="h-3.5 w-5 object-cover rounded" />
                <span className={`font-semibold text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{language.toUpperCase()}</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} ${showMobileSettings ? 'rotate-180' : ''}`} />
              </button>
            </div>
            
            {/* Sidebar Toggle Button - Show when logged in and sidebar is available */}
            {showSidebarToggle && setSidebarOpen && (
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)} 
                className={`p-2.5 rounded-xl min-w-[44px] min-h-[44px] flex flex-col items-center justify-center gap-1.5 transition-colors ${
                  isDarkMode 
                    ? 'bg-white/5 border border-white/10 hover:bg-white/10' 
                    : 'bg-white/80 border border-black/5 hover:bg-gray-100'
                }`} 
                data-testid="sidebar-toggle-btn"
              >
                {sidebarOpen ? (
                  <X className={`h-5 w-5 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} />
                ) : (
                  <>
                    <span className={`block w-5 h-0.5 rounded-full ${isDarkMode ? 'bg-white' : 'bg-gray-700'}`}></span>
                    <span className={`block w-4 h-0.5 rounded-full ${isDarkMode ? 'bg-white' : 'bg-gray-700'}`}></span>
                    <span className={`block w-5 h-0.5 rounded-full ${isDarkMode ? 'bg-white' : 'bg-gray-700'}`}></span>
                  </>
                )}
              </button>
            )}
            
            {/* Hamburger Menu Button - Only show when not logged in */}
            {!user && (
              <button 
                onClick={() => setIsOpen(!isOpen)} 
                className={`p-2.5 rounded-xl min-w-[44px] min-h-[44px] flex flex-col items-center justify-center gap-1.5 transition-colors ${
                  isDarkMode 
                    ? 'bg-white/5 border border-white/10 hover:bg-white/10' 
                    : 'bg-white/80 border border-black/5 hover:bg-gray-100'
                }`} 
                data-testid="mobile-menu-btn"
              >
                {isOpen ? (
                  <X className={`h-5 w-5 ${isDarkMode ? 'text-white' : 'text-gray-700'}`} />
                ) : (
                  <>
                    <span className={`block w-5 h-0.5 rounded-full ${isDarkMode ? 'bg-white' : 'bg-gray-700'}`}></span>
                    <span className={`block w-4 h-0.5 rounded-full ${isDarkMode ? 'bg-white' : 'bg-gray-700'}`}></span>
                    <span className={`block w-5 h-0.5 rounded-full ${isDarkMode ? 'bg-white' : 'bg-gray-700'}`}></span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* iter-236: Mobile Settings — Radix UI Sheet (via shadcn/ui).
            Replaces the previous custom portal that had persistent touch
            issues on real Android devices (Xiaomi/MIUI, Samsung). Radix
            handles focus trapping, outside-click, escape and — critically —
            pointer/touch events correctly on real hardware. */}
        <Sheet open={showMobileSettings} onOpenChange={setShowMobileSettings}>
          <SheetContent
            side="bottom"
            className={`lg:hidden p-0 rounded-t-3xl border-t max-h-[85vh] overflow-y-auto ${
              isDarkMode ? 'bg-[#131B2C] border-white/10' : 'bg-white border-black/5'
            }`}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            data-testid="mobile-settings-sheet"
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div className={`w-10 h-1 rounded-full ${isDarkMode ? 'bg-white/20' : 'bg-black/20'}`} />
            </div>
            <SettingsDropdownContent onClose={() => setShowMobileSettings(false)} />
          </SheetContent>
        </Sheet>

        {/* Mobile Navigation Menu */}
        {isOpen && (
          <div className={`lg:hidden pb-4 space-y-2 border-t pt-4 animate-fade-in-up ${isDarkMode ? 'border-white/10' : 'border-black/5'}`} data-testid="mobile-menu">
            {user && (
              <>
                <Link 
                  to="/dashboard" 
                  className={`block py-3 px-4 rounded-xl transition-all duration-300 text-base font-medium ${
                    isDarkMode 
                      ? 'text-gray-300 hover:text-white hover:bg-white/10' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`} 
                  onClick={() => setIsOpen(false)}
                >
                  {t('dashboard')}
                </Link>
                <Link 
                  to="/stores" 
                  className={`block py-3 px-4 rounded-xl transition-all duration-300 text-base font-medium ${
                    isDarkMode 
                      ? 'text-gray-300 hover:text-white hover:bg-white/10' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`} 
                  onClick={() => setIsOpen(false)}
                >
                  {t('myStores')}
                </Link>
                <Link 
                  to="/account" 
                  className={`block py-3 px-4 rounded-xl transition-all duration-300 text-base font-medium ${
                    isDarkMode 
                      ? 'text-gray-300 hover:text-white hover:bg-white/10' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`} 
                  onClick={() => setIsOpen(false)}
                >
                  {language === 'ar' ? 'حسابي' : 'My Account'}
                </Link>
              </>
            )}
            <div className={`pt-4 space-y-3 border-t ${isDarkMode ? 'border-white/10' : 'border-black/5'}`}>
              {user ? (
                <Button 
                  onClick={handleLogout} 
                  variant="outline" 
                  className={`w-full py-3 text-base rounded-xl ${
                    isDarkMode 
                      ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' 
                      : 'border-red-500 text-red-500 hover:bg-red-50'
                  }`}
                >
                  <LogOut className="h-5 w-5 me-2" />
                  {t('logout')}
                </Button>
              ) : (
                <>
                  <Link to="/login" className="block" onClick={() => setIsOpen(false)}>
                    <Button 
                      variant="outline" 
                      className={`w-full py-3 text-base rounded-xl ${
                        isDarkMode 
                          ? 'border-[#38BDF8] text-[#38BDF8]' 
                          : 'border-[#38BDF8] text-[#38BDF8]'
                      }`}
                    >
                      {t('login')}
                    </Button>
                  </Link>
                  <Link to="/register" className="block" onClick={() => setIsOpen(false)}>
                    <button className="btn-gradient w-full py-3 text-base">
                      {t('register')}
                    </button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
